import { useState, useEffect, useCallback } from "react";
import AppUser from "@toothfairy/shared-ui/AppUser";
import usersHelper from "../API/Users/usersHelper";
import useOnboardingTour from "./useOnboardingTour";

const useReleaseNotes = () => {
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = AppUser.useUser();
  const { isOnboardingComplete } = useOnboardingTour();

  // S3 bucket URL for release notes
  const S3_BUCKET_URL =
    "https://tf-app-release-notes.s3.ap-southeast-2.amazonaws.com";
  const LATEST_RELEASE_NOTES_URL = `${S3_BUCKET_URL}/latest.md`;

  // Fetch the current version from package.json or API
  const fetchCurrentVersion = useCallback(async () => {
    try {
      // Fetch from S3 bucket
      const response = await fetch(LATEST_RELEASE_NOTES_URL);
      const text = await response.text();
      const allVersions = [...text.matchAll(/v(\d+\.\d+\.\d+)/g)];
      const lastVersion = allVersions[allVersions.length - 1];
      if (lastVersion) {
        return lastVersion[1];
      }

      return null;
    } catch (error) {
      console.error("Error fetching current version:", error);
      return null;
    }
  }, []);

  // Fetch release notes content
  const fetchReleaseNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(LATEST_RELEASE_NOTES_URL);
      if (!response.ok) {
        throw new Error("Failed to fetch release notes");
      }

      const content = await response.text();
      setReleaseNotes(content);
      return content;
    } catch (error) {
      console.error("Error fetching release notes:", error);
      setReleaseNotes(null);

      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if user should see release notes
  const checkShouldShowReleaseNotes = useCallback(async () => {
    if (!user?.id) return;

    try {
      const version = await fetchCurrentVersion();
      if (!version) {
        console.log("No version found, skipping release notes check");
        return;
      }

      setCurrentVersion(version);

      // If user hasn't viewed any release notes yet, or if the current version
      // is newer than the last viewed version
      if (
        !user.lastViewedReleaseVersion ||
        compareVersions(version, user.lastViewedReleaseVersion) > 0
      ) {
        await fetchReleaseNotes();
        // Never show release notes while onboarding tours may still appear
        // (e.g. during the guided Joyride tours or after a "Replay onboarding"),
        // otherwise two modals stack on top of one another.
        setShowReleaseNotes(isOnboardingComplete());
      } else {
        setShowReleaseNotes(false);
      }
    } catch (error) {
      console.error("Error checking release notes:", error);
    }
  }, [user, fetchCurrentVersion, fetchReleaseNotes, isOnboardingComplete]);

  // Update the last viewed version in the database
  const markReleaseNotesAsViewed = useCallback(async () => {
    if (!user?.id || !currentVersion) return;

    try {
      await usersHelper.updateUser(user.id, [
        {
          property: "lastViewedReleaseVersion",
          value: currentVersion,
        },
      ]);

      // Update local user object to reflect the change
      if (user) {
        user.lastViewedReleaseVersion = currentVersion;
      }

      setShowReleaseNotes(false);
    } catch (error) {
      console.error("Error updating last viewed release version:", error);
    }
  }, [user, currentVersion]);

  // Version comparison utility
  const compareVersions = (v1, v2) => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }

    return 0;
  };

  // Check on mount and when user changes
  useEffect(() => {
    checkShouldShowReleaseNotes();
  }, [checkShouldShowReleaseNotes]);

  return {
    showReleaseNotes,
    releaseNotes,
    currentVersion,
    isLoading,
    markReleaseNotesAsViewed,
    checkShouldShowReleaseNotes,
    setShowReleaseNotes,
  };
};

export default useReleaseNotes;
